import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, CircularProgress } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';

import { Box } from 'src/components/Box';
import { DashboardLayout } from 'src/components/layouts/LayoutContainers/DashboardLayout';
import { Header } from 'src/pages/Profile/components/Header';
import { Footer } from 'src/components/Footer';
import { WorkerProfileInfoCard } from 'src/components/Cards/WorkerProfileInfoCard';
import { WorkerProfile } from 'src/DTO/WorkerProfile.type';
import { useGetWorkerById } from 'src/api/hooks/useWorker';
import { useGetInterviewHash } from 'src/api/hooks/useInterview';
import { HashAttributes } from 'src/DTO/WorkerInterview.type';
import { getWorkerNameWithAge } from 'src/utils/getWorkerNameWithAge';
import { WorkerActionDialog } from '../Workers/components/types';
import { WorkerActionsDialog } from '../Workers/components/WorkerActionsDialog';
import { WorkerInterviews } from './components/WorkerInterviews/WorkerInterviews';

export const WorkerProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  const [hashAttributes, setHashAttributes] = useState<HashAttributes>();
  const [workerActionDialog, setWorkerActionDialog] = useState<WorkerActionDialog>({
    isOpen: false,
    worker: undefined,
  });

  const [getWorker, { data, loading: getWorkerLoading }] = useGetWorkerById();

  const [getInterviewHash] = useGetInterviewHash({
    onComplete: res => {
      if (res) {
        setHashAttributes(res);
      }
    },
  });

  const handleWorkerDocumentsNavigation = (workerData: WorkerProfile) => {
    history.push({
      pathname: `/worker/${workerData.id}/documents`,
    });
  };

  const openWorkerActionsDialog = (worker: WorkerProfile) => {
    setWorkerActionDialog({ isOpen: true, worker });
  };

  useEffect(() => {
    getInterviewHash();
  }, []);

  useEffect(() => {
    getWorker({ variables: { id } });
  }, [id]);

  if (getWorkerLoading) {
    return (
      <Grid item xs={12}>
        <CircularProgress color="secondary" />
      </Grid>
    );
  }

  return (
    <DashboardLayout>
      {data?.worker && (
        <Header
          imageUrls={data.worker?.imageUrls}
          title={getWorkerNameWithAge(data.worker)}
          subTitle={data.worker ? data.worker.phoneNumber : ''}
          actions={[
            {
              label: t('workerProfile.actions'),
              action: () => openWorkerActionsDialog(data.worker),
            },
            {
              label: t('workerProfile.documents.documents.title'),
              action: () => handleWorkerDocumentsNavigation(data.worker),
            },
          ]}
          style={{ marginInline: '5px' }}
        />
      )}
      <Box mt={5} mb={3}>
        {data?.worker && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <WorkerProfileInfoCard worker={data.worker} />
            </Grid>
            <Grid item xs={12} md={6}>
              <WorkerInterviews
                hashAttributes={hashAttributes}
                defaultInterviewId={data.worker?.interviewId}
                interviews={data.interviews}
              />
            </Grid>
          </Grid>
        )}
      </Box>
      <Footer />
      <WorkerActionsDialog
        isOpen={workerActionDialog.isOpen}
        worker={workerActionDialog.worker}
        handleClose={() => setWorkerActionDialog({ isOpen: false, worker: undefined })}
        showProfileButton={false}
      />
    </DashboardLayout>
  );
};
